import {acceptHMRUpdate, defineStore, getActivePinia} from 'pinia'
import {computed, ref} from "vue";
import {HTTPError} from "ky";
import {useApi} from "@/stores/api";
import {useAuth} from "@/stores/auth";
import {useDictionaries} from "@/stores/dictionaries";

export const useMain = defineStore('main', () => {
    const authStore = useAuth()

    const apiStore = useApi()

    /**
     * Hold the connectivity status
     * @type {Ref<UnwrapRef<string>>}
     */
    const connectivityStatus = ref('ok')
    const routeLoading = ref(true)

    /**
     * True if connectivity is ok
     * @type {ComputedRef<boolean>}
     */
    const isNetworkConnected = computed(() => {
        if (!navigator.onLine) {
            return false;
        }

        return 'ok' === connectivityStatus.value
    })

    const interval = ref()

    const skills = ref([])
    const app = ref({})

    const showCompleteOrganizationProfileNag = ref(false)
    const completeOrganizationProfileNagDismissed = ref(false)

    /**
     * Check connectivity
     * @returns {Promise<void>}
     */
    const checkConnectivity = async () => {
        if (!navigator.onLine) {
            connectivityStatus.value = 'OFFLINE'
        }

        try {
            if (authStore.authenticated) {
                await apiStore.client().head('api/user')
            } else {
                await apiStore.client().head('')
            }
            // didn't throw an error so all good.
            connectivityStatus.value = 'ok'
        } catch (error) {
            //console.log(error)
            const err = await catchCommonErrors(error)
            connectivityStatus.value = err.status
        }
    }

    /**
     * Catch httpr common network errors and update connectivity status
     * @param body
     * @returns {Promise<{errors: {general: string}}|boolean>}
     */
    const catchCommonErrors = async (body) => {
        const error = {status: null, errors: {general: 'Une erreur irrécupérable est survenue. Réessayez.'}}

        if (body instanceof TypeError) {
            connectivityStatus.value = error.status = 'ERR_NETWORK' // update connectivity status
            error.errors = {general: 'Serveur injoignable.'}
        } else if (body instanceof HTTPError) {
            connectivityStatus.value = 'ok'
            error.status = body.response.status
            error.errors = {general: 'Une erreur irrécupérable est survenue. Réessayez.'}

            if (503 === body.response.status) {
                error.errors = {general: 'Le service est temporairement indisponible. Réessayez plus tard.'}
                connectivityStatus.value = body.response.status
            } else if (419 === body.response.status) {
                error.errors = {general: 'Le jeton de sécurité est expiré. Reconnectez vous.'}
            } else if (500 === body.response.status) {
                error.errors = {general: 'Une erreur fatale est survenue. Si cela se reproduit contactez le SPOT.'}
            } else if (429 === body.response.status) {
                error.errors = {general: 'Trop de tentatives. Réessayez plus tard.'}
            } else if (403 === body.response.status) {
                error.errors = {general: 'Opération non permise.'}
            }

        }
        return error
    }

    const intervalCheck = async () => {
        await checkConnectivity()
    }

    const startIntervalCheck = async () => {
        if (!interval.value)
            interval.value = setInterval(intervalCheck, (process.env.NODE_ENV === 'production' || isNetworkConnected.value ? 20000 : 5000))
    }

    const stopIntervalCheck = async () => {
        clearInterval(interval.value)
        interval.value = null
    }

    const loadSkills = async (force = false) => {
        skills.value = await useDictionaries().skills(force)
    }
    const loadAppConfig = async (force = false) => {
        app.value = await useDictionaries().app(force)
    }

    const resetAllStores = () => {
        const activeStores = Object.keys(getActivePinia().state.value);

        activeStores.forEach((store) => {
            if (undefined !== getActivePinia()._s.get(store).$reset) {
                getActivePinia()._s.get(store).$reset();
            }
        });
    };

    const $reset = () => {

    }


    return {
        connectivityStatus,
        routeLoading,
        isNetworkConnected,
        skills,
        app,
        showCompleteOrganizationProfileNag,
        completeOrganizationProfileNagDismissed,
        checkConnectivity,
        catchCommonErrors,
        intervalCheck,
        startIntervalCheck,
        stopIntervalCheck,
        loadSkills,
        loadAppConfig,
        resetAllStores,
        $reset,
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMain, import.meta.hot))
}
